<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import WhatsappContactPreferencesAPI from 'dashboard/api/whatsappContactPreferences';

const props = defineProps({
  contact: {
    type: Object,
    default: () => ({}),
  },
  inboxId: {
    type: [Number, String],
    default: undefined,
  },
  enabled: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();
const store = useStore();

const preference = ref(null);
const isLoadingPreference = ref(false);
const isUpdating = ref(false);
const targetStatus = ref('');
const reason = ref('');
const validationMessage = ref('');
const dialogRef = ref(null);

const canRender = computed(() =>
  Boolean(props.enabled && props.contact?.id && props.inboxId)
);
const customAttributes = computed(() => props.contact?.custom_attributes || {});

const truthyAttribute = value => value === true || value === 'true';

const isLegacyOptedOut = computed(
  () =>
    truthyAttribute(customAttributes.value.opt_out) ||
    truthyAttribute(customAttributes.value.whatsapp_opt_out)
);

const currentStatus = computed(() => {
  if (['opted_in', 'opted_out'].includes(preference.value?.status)) {
    return preference.value.status;
  }
  return isLegacyOptedOut.value ? 'opted_out' : 'unknown';
});

const isOptedOut = computed(() => currentStatus.value === 'opted_out');
const nextStatus = computed(() =>
  isOptedOut.value ? 'opted_in' : 'opted_out'
);

const buttonLabel = computed(() =>
  isOptedOut.value
    ? t('CONTACT_PANEL.WHATSAPP_CONSENT.ACTION_OPT_IN')
    : t('CONTACT_PANEL.WHATSAPP_CONSENT.ACTION_OPT_OUT')
);

const dialogTitle = computed(() =>
  targetStatus.value === 'opted_in'
    ? t('CONTACT_PANEL.WHATSAPP_CONSENT.OPT_IN_TITLE')
    : t('CONTACT_PANEL.WHATSAPP_CONSENT.OPT_OUT_TITLE')
);

const dialogDescription = computed(() =>
  targetStatus.value === 'opted_in'
    ? t('CONTACT_PANEL.WHATSAPP_CONSENT.OPT_IN_DESCRIPTION')
    : t('CONTACT_PANEL.WHATSAPP_CONSENT.OPT_OUT_DESCRIPTION')
);

const confirmLabel = computed(() =>
  targetStatus.value === 'opted_in'
    ? t('CONTACT_PANEL.WHATSAPP_CONSENT.CONFIRM_OPT_IN')
    : t('CONTACT_PANEL.WHATSAPP_CONSENT.CONFIRM_OPT_OUT')
);

const resetDialog = () => {
  reason.value = '';
  validationMessage.value = '';
  targetStatus.value = '';
};

const fetchPreference = async () => {
  if (!canRender.value) {
    preference.value = null;
    return;
  }

  isLoadingPreference.value = true;
  try {
    const response = await WhatsappContactPreferencesAPI.show(
      props.contact.id,
      {
        inbox_id: props.inboxId,
      }
    );
    preference.value = response.data.preference;
  } catch {
    preference.value = null;
  } finally {
    isLoadingPreference.value = false;
  }
};

const openDialog = () => {
  targetStatus.value = nextStatus.value;
  validationMessage.value = '';
  dialogRef.value?.open();
};

const updateConsent = async () => {
  const auditReason = reason.value.trim();
  if (!auditReason) {
    validationMessage.value = t(
      'CONTACT_PANEL.WHATSAPP_CONSENT.REASON_REQUIRED'
    );
    return;
  }

  isUpdating.value = true;
  validationMessage.value = '';
  try {
    const response = await WhatsappContactPreferencesAPI.updatePreference(
      props.contact.id,
      {
        inbox_id: props.inboxId,
        status: targetStatus.value,
        subscription_scope: 'marketing_campaigns',
        reason: auditReason,
      }
    );
    preference.value = response.data.preference;
    await store.dispatch('contacts/show', { id: props.contact.id });
    useAlert(t('CONTACT_PANEL.WHATSAPP_CONSENT.SUCCESS'));
    dialogRef.value?.close();
  } catch (error) {
    useAlert(
      error?.response?.data?.error || t('CONTACT_PANEL.WHATSAPP_CONSENT.ERROR')
    );
  } finally {
    isUpdating.value = false;
  }
};

watch(
  () => [props.contact?.id, props.inboxId, props.enabled],
  () => fetchPreference(),
  { immediate: true }
);
</script>

<template>
  <div v-show="canRender" class="w-full">
    <Button
      class="w-full"
      :label="buttonLabel"
      :icon="isOptedOut ? 'i-lucide-user-check' : 'i-lucide-user-x'"
      :color="isOptedOut ? 'teal' : 'ruby'"
      variant="faded"
      size="sm"
      :is-loading="isLoadingPreference"
      :disabled="isLoadingPreference || isUpdating"
      @click="openDialog"
    />
    <Dialog
      ref="dialogRef"
      width="md"
      :type="targetStatus === 'opted_out' ? 'alert' : 'edit'"
      :title="dialogTitle"
      :description="dialogDescription"
      :confirm-button-label="confirmLabel"
      :is-loading="isUpdating"
      :disable-confirm-button="!reason.trim()"
      @confirm="updateConsent"
      @close="resetDialog"
    >
      <TextArea
        v-model="reason"
        :label="t('CONTACT_PANEL.WHATSAPP_CONSENT.REASON_LABEL')"
        :placeholder="t('CONTACT_PANEL.WHATSAPP_CONSENT.REASON_PLACEHOLDER')"
        :message="validationMessage"
        :message-type="validationMessage ? 'error' : 'info'"
        auto-height
        autofocus
      />
    </Dialog>
  </div>
</template>
